import { AlertCircle, ChevronDown, CreditCard, Loader2 } from "lucide-react";
import { useState } from "react";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { usePayments } from "@/hooks/usePayments";
import { api } from "@/services/api";
import type { Payment } from "@/types/api";
import { canResumePayment, formatCurrency, hasReceipt } from "@/utils/payment";

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const STATUS_PILLS: Record<string, { label: string; className: string }> = {
  completed: { label: "PAID", className: "bg-[#DCF2E4] text-[#1E6F4F]" },
  pending: { label: "PENDING", className: "bg-amber-100 text-amber-800" },
  failed: { label: "FAILED", className: "bg-red-100 text-red-700" },
  cancelled: { label: "CANCELLED", className: "bg-red-100 text-red-700" },
  refunded: { label: "REFUNDED", className: "bg-gray-100 text-gray-600" },
};

function StatusPill({ status }: { status: Payment["status"] }) {
  const pill = STATUS_PILLS[status] ?? STATUS_PILLS.pending;
  return (
    <span
      className={`rounded-full px-3 py-1.5 text-[10.5px] font-bold tracking-[.1em] ${pill.className}`}
    >
      {pill.label}
    </span>
  );
}

export function BillingSection() {
  const { enrollment } = useEnrollment();
  const { payments, programmes, isLoading, error: fetchError, refetch } = usePayments();
  const [isOpen, setIsOpen] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleViewReceipt = async (paymentId: string) => {
    try {
      setActionError(null);
      const { receipt_url } = await api.getPaymentReceiptUrl(paymentId);
      window.open(receipt_url, "_blank");
    } catch (err) {
      console.error("Failed to get receipt URL:", err);
      setActionError(err instanceof Error ? err.message : "Failed to get receipt");
    }
  };

  const handleResumePayment = async (paymentId: string) => {
    try {
      setActionError(null);
      const { checkout_url } = await api.getPaymentCheckoutUrl(paymentId);
      window.location.href = checkout_url;
    } catch (err) {
      console.error("Failed to get checkout URL:", err);
      const message = err instanceof Error ? err.message : "Failed to resume payment";
      setActionError(message);
      if (message.includes("expired")) {
        await refetch();
      }
    }
  };

  const enrolledPayment = enrollment
    ? payments.find((p) => p.programme_id === enrollment.programme_id)
    : undefined;
  const subtitle = enrollment
    ? `${enrollment.programme.name}${enrolledPayment?.status === "completed" ? " · paid in full" : ""}`
    : payments.length > 0
      ? `${payments.length} payment${payments.length === 1 ? "" : "s"}`
      : "No payments yet";

  const displayError = fetchError || actionError;

  return (
    <div className="overflow-hidden rounded-[14px] border border-content-border bg-card">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-4 px-7 py-5 text-left transition-colors hover:bg-content-subtle"
      >
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-[11px] bg-[#E9F1E6]">
          <CreditCard className="h-[19px] w-[19px] text-[#1E6F4F]" strokeWidth={1.7} />
        </div>
        <div className="min-w-0">
          <div className="text-base font-bold text-card-foreground">Payment &amp; billing</div>
          <div className="mt-0.5 truncate text-[13px] text-content-muted">{subtitle}</div>
        </div>
        <ChevronDown
          className={`ml-auto h-[15px] w-[15px] flex-none text-content-muted transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {isOpen && (
        <>
          {displayError && (
            <div className="flex items-center gap-3 border-t border-content-border/60 bg-red-50 px-7 py-4 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {displayError}
            </div>
          )}

          {enrollment && (
            <div className="grid grid-cols-[130px_1fr_auto] items-center gap-4 border-t border-content-border/60 px-7 py-[18px] sm:grid-cols-[170px_1fr_auto]">
              <div className="text-[13px] font-bold text-[#5C6E63]">Plan</div>
              <div className="text-[15px] font-semibold text-card-foreground">
                {enrollment.programme.name} · one-time payment
              </div>
              {enrolledPayment ? <StatusPill status={enrolledPayment.status} /> : <span />}
            </div>
          )}

          <div className="border-t border-content-border/60 px-7 py-[18px]">
            <div className="text-[11px] font-bold uppercase tracking-[.16em] text-content-muted">
              Payment History
            </div>
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-content-muted" />
              </div>
            ) : payments.length > 0 ? (
              payments.map((payment, i) => (
                <div
                  key={payment.id}
                  className={`flex items-center gap-4 py-[15px] ${i < payments.length - 1 ? "border-b border-content-border/60" : ""}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[14.5px] font-bold text-card-foreground">
                      {programmes.get(payment.programme_id)?.name ?? "Programme"} enrolment
                    </div>
                    <div className="mt-0.5 text-[12.5px] text-content-muted">
                      {formatDate(payment.completed_at ?? payment.created_at)}
                      {payment.promo_code && ` · code ${payment.promo_code}`}
                    </div>
                  </div>
                  {payment.status !== "completed" && <StatusPill status={payment.status} />}
                  <div className="text-[15px] font-bold text-card-foreground">
                    {formatCurrency(payment.amount_total ?? payment.amount, payment.currency)}
                  </div>
                  {hasReceipt(payment.status, payment.amount_total) && (
                    <button
                      onClick={() => handleViewReceipt(payment.id)}
                      className="rounded-[9px] border border-content-border bg-card px-4 py-2 text-[12.5px] font-bold text-content-foreground transition-colors hover:border-brand/50"
                    >
                      Receipt ↓
                    </button>
                  )}
                  {canResumePayment(payment.status) && (
                    <button
                      onClick={() => handleResumePayment(payment.id)}
                      className="rounded-[9px] border border-brand bg-card px-4 py-2 text-[12.5px] font-bold text-[#1E6F4F] transition-colors hover:bg-brand/5"
                    >
                      Resume payment
                    </button>
                  )}
                </div>
              ))
            ) : (
              <p className="py-4 font-serif text-[13px] italic text-content-muted">
                No payments yet — when you enrol in a paid programme, your payments and receipts
                will appear here.
              </p>
            )}
          </div>

          <div className="border-t border-content-border/60 bg-content-subtle px-7 py-4 text-[13px] text-content-muted">
            Need an invoice or a refund?{" "}
            <a
              href="mailto:info@dominionfinancialreview.com"
              className="font-bold text-[#1E6F4F] hover:text-brand"
            >
              Contact support
            </a>
            .
          </div>
        </>
      )}
    </div>
  );
}
